/**
 * Timeline Scroll Jacking
 * Implements horizontal scroll-jacking for the timeline section
 * When user scrolls vertically in the timeline section, it scrolls horizontally instead
 */
import { runWhenPageReady } from '../utils/PageLifecycle';

class TimelineScrollJacking {
  constructor() {
    this.section = document.querySelector('.timeline-section');
    this.scrollWrapper = document.querySelector('.timeline-section__scroll-wrapper');
    
    if (!this.section || !this.scrollWrapper) {
      return;
    }

    this.isScrolling = false;
    this.scrollDirection = 0;
    this.scrollAmount = 0;
    this.maxScroll = 0;
    
    this.init();
  }

  init() {
    // Calculate max horizontal scroll
    this.calculateMaxScroll();
    
    // Watch for when section enters/exits viewport
    this.setupIntersectionObserver();
    
    // Handle window resize
    window.addEventListener('resize', () => {
      this.calculateMaxScroll();
    });
  }

  calculateMaxScroll() {
    this.maxScroll = this.scrollWrapper.scrollWidth - this.scrollWrapper.clientWidth;
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '-20% 0px -20% 0px', // Only trigger when section is more centered
      threshold: 0.3 // Require 30% visibility
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.activateScrollJacking();
        } else {
          this.deactivateScrollJacking();
        }
      });
    }, options);

    observer.observe(this.section);
  }

  activateScrollJacking() {
    if (this.isScrolling) return;
    
    this.isScrolling = true;
    this.section.classList.add('is-scrolling');
    
    // Add wheel event listener
    window.addEventListener('wheel', this.handleWheel, { passive: false });
    
    // Add touch events for mobile
    let touchStartX = 0;
    let touchStartY = 0;
    
    this.handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };
    
    this.handleTouchMove = (e) => {
      if (!touchStartX || !touchStartY) return;
      
      const touchEndX = e.touches[0].clientX;
      const touchEndY = e.touches[0].clientY;
      
      const diffX = touchStartX - touchEndX;
      const diffY = touchStartY - touchEndY;
      
      // If vertical swipe is dominant, convert to horizontal scroll
      if (Math.abs(diffY) > Math.abs(diffX)) {
        e.preventDefault();
        this.scrollWrapper.scrollLeft += diffY * 0.5;
      }
      
      touchStartX = touchEndX;
      touchStartY = touchEndY;
    };
    
    this.section.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.section.addEventListener('touchmove', this.handleTouchMove, { passive: false });
  }

  deactivateScrollJacking() {
    if (!this.isScrolling) return;
    
    this.isScrolling = false;
    this.section.classList.remove('is-scrolling');
    
    // Remove event listeners
    window.removeEventListener('wheel', this.handleWheel);
    
    if (this.handleTouchStart) {
      this.section.removeEventListener('touchstart', this.handleTouchStart);
    }
    
    if (this.handleTouchMove) {
      this.section.removeEventListener('touchmove', this.handleTouchMove);
    }
  }

  handleWheel = (e) => {
    // Get current scroll position
    const currentScroll = this.scrollWrapper.scrollLeft;
    
    // Determine if we can scroll horizontally
    const canScrollLeft = currentScroll > 0;
    const canScrollRight = currentScroll < this.maxScroll - 1; // -1 for rounding
    
    // Vertical scroll delta
    const delta = e.deltaY;
    
    // Only prevent default if we can actually scroll in that direction
    if (delta > 0 && canScrollRight) {
      // Scrolling down/right and there's more content
      e.preventDefault();
      e.stopPropagation();
      
      const scrollAmount = delta * 0.6;
      this.scrollWrapper.scrollBy({
        left: scrollAmount,
        behavior: 'auto'
      });
      
      return false;
    } else if (delta < 0 && canScrollLeft) {
      // Scrolling up/left and there's more content
      e.preventDefault();
      e.stopPropagation();
      
      const scrollAmount = delta * 0.6;
      this.scrollWrapper.scrollBy({
        left: scrollAmount,
        behavior: 'auto'
      });
      
      return false;
    }
    // At the boundaries or scrolling in wrong direction, allow normal scroll
  }
}

runWhenPageReady(() => new TimelineScrollJacking());

export default TimelineScrollJacking;
